import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { newId, type PhaseId, type ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * The five selectable Version 1 phases (D-012).
 *
 * General recomposition is deliberately absent and must not be added without a
 * new decision. The rules registry dispatches on exactly this union and has no
 * default branch (D-043).
 */
export const PHASE_TYPES = [
  'fatLoss',
  'maintenance',
  'leanBulk',
  'miniCut',
  'aestheticPrep',
] as const

export type PhaseType = (typeof PHASE_TYPES)[number]

export type PhaseStatus = 'active' | 'completed'

export interface PhaseRecord extends RecordEnvelope {
  readonly id: PhaseId
  /**
   * The project this phase belongs to (D-062).
   *
   * Optional in the type only. Every stored phase has one: the schema v2
   * migration stamps it onto existing history, and every write path creates the
   * project first. It stays optional here because the builders below take a
   * `PhaseInput` that the Project Setup wizard fills in at sub-checkpoint 2.5c;
   * until then the caller attaches it, rather than a placeholder being invented
   * inside the builder.
   */
  readonly projectId?: ProjectId
  readonly type: PhaseType
  readonly startDate: CalendarDate
  readonly plannedEndDate: CalendarDate
  /** Target weekly weight change as a percentage of body weight, signed:
      negative for loss, positive for gain. Configured per phase and stored on
      the record, never a hidden universal constant (D-004 risk note). The
      approved defaults that pre-fill it are in `SUGGESTED_WEEKLY_CHANGE_PERCENT`
      below; the engine reads this field, not that map. */
  readonly targetWeeklyChangeMinPercent: number
  readonly targetWeeklyChangeMaxPercent: number
  readonly status: PhaseStatus
  readonly actualEndDate?: CalendarDate
  readonly notes?: string
  /** Set once the user has acknowledged the planned-end warning (D-020). */
  readonly plannedEndAcknowledged?: boolean
  /** Why the phase ended, recorded at the explicit transition decision (D-020). */
  readonly transitionReason?: string
  /**
   * Corrections made after the fact, oldest first (D-070).
   *
   * Kept alongside the current values rather than replacing them, in the same
   * shape as `correctedFromKg` on a weight entry (D-035). An export can then
   * show that a date was amended rather than presenting a late correction as
   * though it were the original record — which is exactly what a silent
   * overwrite would do, and why D-070 rejected one.
   */
  readonly corrections?: readonly PhaseCorrection[]
}

/** The fields D-070 allows a correction to touch. Type and range are absent. */
export type CorrectablePhaseField = 'startDate' | 'actualEndDate' | 'notes' | 'transitionReason'

export interface PhaseCorrection {
  readonly field: CorrectablePhaseField
  /** The value before the correction. Absent when the field was empty. */
  readonly from?: string
  readonly to?: string
  readonly correctedAt: IsoTimestamp
}

/**
 * What a historical correction may change (D-070).
 *
 * **The freezing of target range and type is enforced by this type**, not by a
 * runtime check: there is no field here to carry them, so a correction cannot
 * express the change at all. Changing a past target range would silently rewrite
 * whether historical weeks were "on track" — rewriting a conclusion the user has
 * already read — and the type belongs to the rules registry (D-043).
 *
 * `status` is absent for the same reason, which is what makes "an edit may not
 * reopen a completed phase" structural rather than defended.
 */
export interface PhaseHistoryPatch {
  readonly startDate?: string
  readonly actualEndDate?: string
  readonly notes?: string
  readonly transitionReason?: string
}

export interface PhaseInput {
  /** Set by every caller that has a project; carried through unchanged. */
  readonly projectId?: ProjectId
  readonly type: string
  readonly startDate: string
  readonly plannedEndDate: string
  readonly targetWeeklyChangeMinPercent: number
  readonly targetWeeklyChangeMaxPercent: number
  readonly notes?: string
}

export function isPhaseType(value: string): value is PhaseType {
  return (PHASE_TYPES as readonly string[]).includes(value)
}

/** Display labels. Presentation only — never persisted, never compared. */
export const PHASE_LABELS: Readonly<Record<PhaseType, string>> = {
  fatLoss: 'Fat loss',
  maintenance: 'Maintenance',
  leanBulk: 'Lean bulk',
  miniCut: 'Mini cut',
  aestheticPrep: 'Short aesthetic preparation',
}

export const PHASE_DESCRIPTIONS: Readonly<Record<PhaseType, string>> = {
  fatLoss: 'Reduce fat while preserving muscle, strength, and recovery.',
  maintenance: 'Stabilise weight, recover from dieting, and keep strength and routine.',
  leanBulk: 'Gain weight slowly to support muscle and performance, with waist controlled.',
  miniCut: 'Lose a modest amount of fat over a short, explicit period.',
  aestheticPrep: 'Look leaner for a specific date, staying conservative and health-first.',
}

/**
 * Suggested target weekly weight change, as a percentage of body weight.
 *
 * Signed: negative is loss, positive is gain, and `min` is always the lower
 * number rather than the smaller magnitude.
 *
 * Fat loss comes from the handoff (approximately 0.4%–0.7% loss per week). The
 * other four were **approved as B-004 on 2026-08-06 and are recorded in D-065**,
 * which carries the reasoning for each. They are not derivable from the handoff,
 * which gives only qualitative priorities for those phases, and they were not
 * invented here.
 *
 * **These are suggestions, not constraints.** Every phase record stores its own
 * range, the user may change it, and the rules engine judges a week against the
 * stored value — never against this map. That is what keeps an approved default
 * from becoming a hidden universal constant.
 *
 * The map is total over `PhaseType`, so adding a sixth phase without deciding
 * its range is a compile error rather than a silent gap.
 */
export const SUGGESTED_WEEKLY_CHANGE_PERCENT: Readonly<
  Record<PhaseType, { readonly min: number; readonly max: number }>
> = {
  /* Handoff §14: approximately 0.4%–0.7% of body weight lost per week. */
  fatLoss: { min: -0.7, max: -0.4 },
  /* A band around zero. Wide enough that ordinary water and food-volume noise
     does not read as drift on a seven-day average, tight enough that a real
     trend surfaces within two or three weeks (D-065). */
  maintenance: { min: -0.25, max: 0.25 },
  /* Slow, controlled gain — the handoff's "gain weight slowly, limit
     unnecessary fat gain" (D-065). */
  leanBulk: { min: 0.15, max: 0.35 },
  /* Faster than ordinary fat loss and explicitly bounded, which is what the
     handoff means by "faster but bounded rate of loss" (D-065). */
  miniCut: { min: -1.0, max: -0.7 },
  /* Gentler than ordinary fat loss. This phase runs into a fixed date where
     fullness and presentation matter, and the handoff prioritises safety and
     consistency over rate (D-065). */
  aestheticPrep: { min: -0.5, max: -0.3 },
}

export function validatePhase(input: PhaseInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (!isPhaseType(input.type)) {
    issues.push(issue('type', `Phase type must be one of: ${PHASE_TYPES.join(', ')}.`))
  }

  const start = toCalendarDate(input.startDate)
  const end = toCalendarDate(input.plannedEndDate)

  if (start === undefined) {
    issues.push(issue('startDate', 'Start date must be a real date in YYYY-MM-DD form.'))
  }
  if (end === undefined) {
    issues.push(issue('plannedEndDate', 'Planned end date must be a real date in YYYY-MM-DD form.'))
  }
  if (start !== undefined && end !== undefined && unwrapDate(end) < unwrapDate(start)) {
    // Lexical comparison is correct for zero-padded YYYY-MM-DD.
    issues.push(issue('plannedEndDate', 'Planned end date must not be before the start date.'))
  }

  const { targetWeeklyChangeMinPercent: min, targetWeeklyChangeMaxPercent: max } = input
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    issues.push(issue('targetWeeklyChange', 'Target weekly change must be finite numbers.'))
  } else if (min > max) {
    issues.push(
      issue('targetWeeklyChange', 'Minimum target weekly change must not exceed the maximum.'),
    )
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/**
 * Identity carried across an edit.
 *
 * Editing a phase must not mint a new one, and must not reopen a completed one:
 * the caller supplies the existing identity and the builder preserves it. That
 * keeps `completePhase` the only route from active to completed, which matters
 * because closing a phase is an explicit transition decision (D-020).
 *
 * `plannedEndAcknowledged` is deliberately NOT carried here. Moving the planned
 * end date is exactly the case where a previous acknowledgement no longer
 * applies, and the caller decides that rather than the builder.
 */
export interface ExistingPhaseIdentity {
  readonly id: PhaseId
  readonly projectId?: ProjectId
  readonly status: PhaseStatus
  readonly createdAt: IsoTimestamp
  readonly actualEndDate?: CalendarDate
  /** History, carried across an edit rather than rebuilt (D-070). */
  readonly transitionReason?: string
  readonly corrections?: readonly PhaseCorrection[]
}

/** Build a validated phase record without touching storage. */
export function buildPhaseRecord(
  input: PhaseInput,
  now: IsoTimestamp,
  existing?: ExistingPhaseIdentity,
): Result<PhaseRecord, ValidationIssues> {
  const issues = validatePhase(input)
  if (issues.length > 0) return err(issues)

  const startDate = toCalendarDate(input.startDate)
  const plannedEndDate = toCalendarDate(input.plannedEndDate)
  if (startDate === undefined || plannedEndDate === undefined) {
    return err([issue('phase', 'Phase dates failed construction.')])
  }

  /* An edit keeps the phase in the project it already belonged to; moving a
     phase between projects is not an operation this app has. */
  const projectId = existing?.projectId ?? input.projectId

  return ok({
    id: existing?.id ?? newId<'PhaseId'>(),
    ...(projectId === undefined ? {} : { projectId }),
    type: input.type as PhaseType,
    startDate,
    plannedEndDate,
    targetWeeklyChangeMinPercent: input.targetWeeklyChangeMinPercent,
    targetWeeklyChangeMaxPercent: input.targetWeeklyChangeMaxPercent,
    status: existing?.status ?? 'active',
    ...(existing?.actualEndDate === undefined ? {} : { actualEndDate: existing.actualEndDate }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    /* Carried, not rebuilt. An ordinary edit to the active phase must not erase
       why an earlier phase ended or the record of a correction — both are
       history, and D-070 says nothing about them is deletable. */
    ...(existing?.transitionReason === undefined
      ? {}
      : { transitionReason: existing.transitionReason }),
    ...(existing?.corrections === undefined ? {} : { corrections: existing.corrections }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * Whether a phase has reached its planned end (D-020).
 *
 * Pure and inclusive: the planned end date itself counts as reached, because it
 * is the last day of the phase rather than the first day after it.
 *
 * **Reaching a planned end changes nothing.** This answers only whether to show
 * the warning; every consequence of it is an explicit user decision. Nothing in
 * this codebase may transition, extend or re-target a phase from this boolean.
 */
export function hasReachedPlannedEnd(phase: PhaseRecord, today: CalendarDate): boolean {
  if (phase.status !== 'active') return false
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  return unwrapDate(today) >= unwrapDate(phase.plannedEndDate)
}

/**
 * The span a phase occupies, inclusive at both ends.
 *
 * A completed phase ends when it actually ended; an active one is bounded by its
 * planned end, which is the only end it has. Inclusive matters: two phases
 * sharing a single day overlap, so a handover starts the day *after* the one
 * before it ended. Treating the end as exclusive would let two phases both claim
 * the same day's weigh-in.
 */
export function phaseSpan(phase: PhaseRecord): { readonly from: string; readonly to: string } {
  return {
    from: unwrapDate(phase.startDate),
    to: unwrapDate(phase.actualEndDate ?? phase.plannedEndDate),
  }
}

function spansOverlap(
  left: { readonly from: string; readonly to: string },
  right: { readonly from: string; readonly to: string },
): boolean {
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  return left.from <= right.to && right.from <= left.to
}

/**
 * Whether a phase would overlap any other (D-070).
 *
 * Shared by the correction path, the active-phase editor and the create path
 * deliberately. The constraint is a property of the history, not of one screen,
 * and enforcing it in only one place would leave the others free to create the
 * overlap.
 *
 * **`createPhase` joined at D-103 §3, and its absence there was not a bug until
 * then.** Creation had exactly one caller — project setup — which runs against
 * an empty `phases` store, so there was never another span to clash with. The
 * next-phase flow makes creation the path that appends to a history for the
 * first time, which is precisely the case the constraint exists for. D-103's own
 * consequences say the rules are "shared by both write paths"; they were, and
 * creation was a third path that had never needed them.
 */
export function overlappingPhase(
  candidate: PhaseRecord,
  others: readonly PhaseRecord[],
): PhaseRecord | undefined {
  const span = phaseSpan(candidate)
  return others.find((other) => other.id !== candidate.id && spansOverlap(span, phaseSpan(other)))
}

/**
 * The four constraints D-070 requires, each a real way to corrupt the history.
 *
 * Two of them are structural rather than checked here: identity is preserved
 * because the caller supplies the stored record, and type, range and status are
 * frozen because `PhaseHistoryPatch` has nowhere to put them. What remains are
 * the two that depend on values — dates that parse and order correctly, and no
 * overlap with another phase.
 */
export function validatePhaseHistoryPatch(
  phase: PhaseRecord,
  patch: PhaseHistoryPatch,
  others: readonly PhaseRecord[],
): ValidationIssues {
  const issues: ValidationIssue[] = []

  const startDate =
    patch.startDate === undefined ? phase.startDate : toCalendarDate(patch.startDate)
  if (startDate === undefined) {
    issues.push(issue('startDate', 'Start date must be a real date in YYYY-MM-DD form.'))
  }

  /* An explicit empty string clears a value; `undefined` leaves it untouched.
     The two are different edits and must not be conflated. */
  const clearingEnd = patch.actualEndDate === ''
  const actualEndDate =
    patch.actualEndDate === undefined || clearingEnd
      ? phase.actualEndDate
      : toCalendarDate(patch.actualEndDate)

  if (patch.actualEndDate !== undefined && !clearingEnd && actualEndDate === undefined) {
    issues.push(issue('actualEndDate', 'End date must be a real date in YYYY-MM-DD form.'))
  }

  /* A completed phase with no end date is incoherent — that date is what says
     when it stopped. A correction supplies a better one; it never removes the
     only one there is. */
  if (clearingEnd && phase.status === 'completed') {
    issues.push(
      issue(
        'actualEndDate',
        'A completed phase must keep an end date. Correct it rather than clearing it.',
      ),
    )
  }

  if (
    startDate !== undefined &&
    actualEndDate !== undefined &&
    unwrapDate(actualEndDate) < unwrapDate(startDate)
  ) {
    issues.push(issue('actualEndDate', 'End date must not be before the start date.'))
  }

  if (startDate !== undefined) {
    const corrected: PhaseRecord = {
      ...phase,
      startDate,
      ...(actualEndDate === undefined ? {} : { actualEndDate }),
    }
    const clash = overlappingPhase(corrected, others)
    if (clash !== undefined) {
      const span = phaseSpan(clash)
      issues.push(
        issue(
          'startDate',
          `Those dates overlap the ${PHASE_LABELS[clash.type]} phase running ${span.from} to ${span.to}. Phases may not overlap.`,
        ),
      )
    }
  }

  return issues
}

const CORRECTABLE_FIELDS: readonly CorrectablePhaseField[] = [
  'startDate',
  'actualEndDate',
  'notes',
  'transitionReason',
]

/**
 * Apply a correction, recording what it replaced (D-070).
 *
 * Nothing is erased: every changed field appends a `PhaseCorrection` holding the
 * previous value and the time of the change. A field submitted with the value it
 * already had records nothing, so opening a form and saving it unchanged does
 * not manufacture a history of corrections that never happened.
 */
export function applyPhaseHistoryPatch(
  phase: PhaseRecord,
  patch: PhaseHistoryPatch,
  now: IsoTimestamp,
  others: readonly PhaseRecord[] = [],
): Result<PhaseRecord, ValidationIssues> {
  const issues = validatePhaseHistoryPatch(phase, patch, others)
  if (issues.length > 0) return err(issues)

  const corrections: PhaseCorrection[] = []
  let next: PhaseRecord = phase

  for (const field of CORRECTABLE_FIELDS) {
    const requested = patch[field]
    if (requested === undefined) continue

    const before = next[field] === undefined ? undefined : String(next[field])
    const after = requested === '' ? undefined : requested
    if (before === after) continue

    corrections.push({
      field,
      ...(before === undefined ? {} : { from: before }),
      ...(after === undefined ? {} : { to: after }),
      correctedAt: now,
    })

    next = after === undefined ? withoutField(next, field) : withField(next, field, after)
  }

  if (corrections.length === 0) return ok(phase)

  return ok({
    ...next,
    corrections: [...(phase.corrections ?? []), ...corrections],
    updatedAt: now,
  })
}

function withField(phase: PhaseRecord, field: CorrectablePhaseField, value: string): PhaseRecord {
  if (field === 'notes') return { ...phase, notes: value }
  if (field === 'transitionReason') return { ...phase, transitionReason: value }

  /* Validation has already rejected an unparseable date, so this only carries
     the branding across. */
  const date = toCalendarDate(value) as CalendarDate
  return field === 'startDate' ? { ...phase, startDate: date } : { ...phase, actualEndDate: date }
}

function withoutField(phase: PhaseRecord, field: CorrectablePhaseField): PhaseRecord {
  const { [field]: _removed, ...rest } = phase
  return rest as PhaseRecord
}
